import React, { useState } from "react";
import {
    Card,
    CardHeader,
    CardContent,
    IconButton,
    Box,
    Typography,
    Chip,
    Collapse,
    Avatar,
    List,
    ListItem,
    ListItemText,
    Divider,
    Tooltip
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { IMenuListResponse } from "../../../../model/admin-portal/response/menu-list-response";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface MenuCardProps {
    menu: IMenuListResponse;
    onEdit: (menu: IMenuListResponse) => void;
    onDelete: (id: number) => void;
}

export const MenuCard: React.FC<MenuCardProps> = ({ menu, onEdit, onDelete }) => {
    const [expanded, setExpanded] = useState(false);
    const isHomeMenu = menu.slug?.toLowerCase() === 'home';

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: menu.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        marginBottom: '16px'
    };

    return (
        <Card ref={setNodeRef} style={style} sx={{ mb: 2 }}>
            <CardHeader
                avatar={
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box {...attributes} {...listeners} sx={{ cursor: 'grab', display: 'flex' }}>
                            <DragIndicatorIcon color="action" />
                        </Box>
                        {menu.iconUrl && (
                            <Avatar src={menu.iconUrl} sx={{ width: 32, height: 32 }} />
                        )}
                    </Box>
                }
                title={
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6">{menu.title}</Typography>
                        {menu.isDefault && (
                            <Chip label="Padrão" size="small" color="primary" />
                        )}
                        {!menu.visible && (
                            <Tooltip title="Menu oculto">
                                <VisibilityOffIcon color="disabled" fontSize="small" />
                            </Tooltip>
                        )}
                    </Box>
                }
                subheader={
                    <Box>
                        <Typography variant="body2" color="textSecondary">
                            Slug: {menu.slug} | Ordem: {menu.order} | {menu.items.length} {menu.items.length === 1 ? 'item' : 'itens'}
                        </Typography>
                    </Box>
                }
                action={
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                            size="small"
                            onClick={() => setExpanded(!expanded)}
                        >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Tooltip title={isHomeMenu ? 'Menu HOME não pode ser editado' : 'Editar menu'}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => onEdit(menu)}
                                    disabled={isHomeMenu}
                                >
                                    <EditIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={isHomeMenu ? 'Menu HOME não pode ser excluído' : 'Excluir menu'}>
                            <span>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => onDelete(menu.id)}
                                    disabled={isHomeMenu}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                }
            />

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Itens do Menu
                    </Typography>

                    {menu.items.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                            Nenhum item cadastrado
                        </Typography>
                    ) : (
                        <List dense>
                            {menu.items.map((item, index) => (
                                <React.Fragment key={item.id}>
                                    <ListItem>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {item.iconUrl && (
                                                        <Avatar src={item.iconUrl} sx={{ width: 24, height: 24 }} />
                                                    )}
                                                    <span>{item.title}</span>
                                                    {item.isDefault && (
                                                        <Chip label="Padrão" size="small" color="primary" />
                                                    )}
                                                    {!item.visible && (
                                                        <VisibilityOffIcon color="disabled" fontSize="small" />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Typography variant="caption" color="textSecondary">
                                                    {item.url || item.externalUrl || 'Sem URL'} | Ordem: {item.order}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    {index < menu.items.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </CardContent>
            </Collapse>
        </Card>
    );
};
